import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { getFirestore, collection, query, orderBy, limit, getDocs, addDoc, serverTimestamp, getDoc, doc, updateDoc, deleteDoc } from 'firebase/firestore';
import { getAuth, signInAnonymously } from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { MessageCard } from './MessageCard';
import { MessageInput } from './MessageInput';
import { getResponsiveValue } from '../../styles/breakpoints';
import { useUser } from '../../components/LoginPersistence';
import { addFileToUserUploads, getOrCreateDiscussionBoardMediaSubfolder } from '../../utils/fileManagerUtils';
import { generateFileName } from '../../utils/userUploadsManager';

const DEFAULT_PROFILE_PHOTOS = [
 require('../../../assets/profile_photos/koi.png'),
 require('../../../assets/profile_photos/bear.png'),
];

const getRandomDefaultPhoto = () => {
  const randomIndex = Math.floor(Math.random() * DEFAULT_PROFILE_PHOTOS.length);
  return DEFAULT_PROFILE_PHOTOS[randomIndex];
};

export function DiscussionBoard({ venueId }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const messagesPerPage = 10;
  const auth = getAuth();
  const { user: userData } = useUser();

  useEffect(() => {
    loadMessages();
  }, [venueId]);

  const loadMessages = async () => {
    try {
      const db = getFirestore();
      const q = query(
        collection(db, `venues/${venueId}/discussions`),
        orderBy('timestamp', 'desc'),
        limit(messagesPerPage)
      );
      
      const querySnapshot = await getDocs(q);
      const messageData = [];
      
      querySnapshot.forEach((doc) => {
        messageData.push({ id: doc.id, ...doc.data() });
      });
      
      setMessages(messageData);
      setLoading(false);
    } catch (error) {
      console.error('Error loading messages:', error);
      setLoading(false);
    }
  };

  const uploadPhoto = async (photoFile) => {
    if (!photoFile) return null;

    try {
      const storage = getStorage();
      const timestamp = new Date().getTime();
      const fileName = `${auth.currentUser.uid}_${timestamp}_${photoFile.name}`;
      const storageRef = ref(storage, `discussionPhotos/${venueId}/${fileName}`);
      
      await uploadBytes(storageRef, photoFile);
      const photoURL = await getDownloadURL(storageRef);
      
      // Add photo to Discussion Board Media subfolder within User Uploads in parallel (don't block the message posting)
      const displayFileName = generateFileName(photoURL, 'photo', 'discussion', 0);
      console.log('🔄 Creating Discussion Board Media subfolder for user:', auth.currentUser.uid);
      getOrCreateDiscussionBoardMediaSubfolder(auth.currentUser.uid)
        .then(discussionSubfolderId => {
          console.log('✅ Got Discussion Board Media subfolder ID:', discussionSubfolderId);
          return addFileToUserUploads(auth.currentUser.uid, displayFileName, photoURL, 'discussion', {
            venueId: venueId,
            messageType: 'discussion_photo',
            uploadDate: new Date().toISOString(),
            targetFolderId: discussionSubfolderId
          });
        })
        .then(fileId => {
          console.log('✅ Successfully added discussion photo to subfolder, file ID:', fileId);
        })
        .catch(error => {
          console.error('❌ Failed to add discussion photo to Discussion Board Media subfolder:', error);
          // Don't throw - we don't want to break the message posting
        });
      
      return photoURL;
    } catch (error) {
      console.error('Error uploading photo:', error);
      alert('Failed to upload photo. Please try again.');
      return null;
    }
  };

  const handleSubmitMessage = async (message, photoFile, isAnonymous = false) => {
    if (!message.trim() && !photoFile) {
      alert('Please enter a message or attach a photo');
      return;
    }

    try {
      // Allow posting without an account: sign in anonymously if not logged in
      if (!auth.currentUser) {
        await signInAnonymously(auth);
      }

      const db = getFirestore();

      // Upload photo if available (uses auth.currentUser.uid from anonymous or real auth)
      let photoURL = null;
      if (photoFile) {
        photoURL = await uploadPhoto(photoFile);
      }

      // When not logged in we signed in anonymously; treat as anonymous post. Same if user chose "Post Anonymously".
      const postAsAnonymous = isAnonymous || !userData;
      await addDoc(collection(db, `venues/${venueId}/discussions`), {
        userId: auth.currentUser.uid,
        username: postAsAnonymous ? 'Anonymous' : (userData.username || userData.firstName || 'Anonymous'),
        userAvatar: postAsAnonymous ? getRandomDefaultPhoto() : (userData.photoURL || getRandomDefaultPhoto()),
        message: message.trim(),
        photoURL: photoURL,
        timestamp: serverTimestamp(),
        email: userData?.email ?? null,
        isAnonymous: postAsAnonymous
      });

      loadMessages();
    } catch (error) {
      console.error('Error posting message:', error);
      alert('Failed to post message. Please try again.');
    }
  };

  const handleEditMessage = async (messageId, newMessage) => {
    if (!auth.currentUser) {
      alert('Please login to edit messages');
      return;
    }

    try {
      const db = getFirestore();
      const messageRef = doc(db, `venues/${venueId}/discussions/${messageId}`);
      
      // Get the message first to verify ownership
      const messageDoc = await getDoc(messageRef);
      if (!messageDoc.exists()) {
        alert('Message not found');
        return;
      }

      const messageData = messageDoc.data();
      if (messageData.userId !== auth.currentUser.uid) {
        alert('You can only edit your own messages');
        return;
      }
      
      await updateDoc(messageRef, {
        message: newMessage,
        editedAt: serverTimestamp()
      });
      
      loadMessages();
    } catch (error) {
      console.error('Error editing message:', error);
      alert('Failed to edit message. Please try again.');
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!auth.currentUser) {
      alert('Please login to delete messages');
      return;
    }

    try {
      const db = getFirestore();
      const messageRef = doc(db, `venues/${venueId}/discussions/${messageId}`);
      
      // Get the message first to verify ownership
      const messageDoc = await getDoc(messageRef);
      if (!messageDoc.exists()) {
        alert('Message not found');
        return;
      }

      const messageData = messageDoc.data();
      if (messageData.userId !== auth.currentUser.uid) {
        alert('You can only delete your own messages');
        return;
      }
      
      // Note: We're not deleting photos from storage for simplicity
      // In a production app, you would want to delete the photo as well
      
      await deleteDoc(messageRef);
      
      loadMessages();
    } catch (error) {
      console.error('Error deleting message:', error);
      alert('Failed to delete message. Please try again.');
    }
  };

  const sendButtonStyle = {
    backgroundColor: '#000000',
    padding: 10,
    borderRadius: 5,
    marginLeft: 10,
    minWidth: 80,
    alignItems: 'center',
    justifyContent: 'center',
    transition: 'background-color 0.3s ease',
    ':hover': {
      backgroundColor: '#333333',
    },
  };

  if (loading) {
    return <LoadingText>Loading messages...</LoadingText>;
  }

  return (
    <BoardContainer>
      <MessagesContainer>
        {messages.length === 0 ? (
          <NoMessagesText>Be the first to start a discussion!</NoMessagesText>
        ) : (
          messages.map(message => (
            <MessageCard 
              key={message.id}
              messageId={message.id}
              {...message}
              isCurrentUser={auth.currentUser?.uid === message.userId}
              onEdit={handleEditMessage}
              onDelete={handleDeleteMessage}
            />
          ))
        )}
      </MessagesContainer>
      <MessageInputContainer>
        <MessageInput onSubmit={handleSubmitMessage} />
      </MessageInputContainer>
    </BoardContainer>
  );
}

const BoardContainer = styled.div`
  display: flex;
  width: 100%;
  flex-direction: column;
  height: 100%;
  gap: ${getResponsiveValue({
    mobile: '15px',
    tablet: '20px',
    laptop: '20px',
    desktop: '20px'
  })};
  align-items: center;
`;

const MessagesContainer = styled.div`
  display: flex;
  flex-direction: column-reverse;
  gap: ${getResponsiveValue({
    mobile: '10px',
    tablet: '15px',
    laptop: '15px',
    desktop: '15px'
  })};
  paddingVertical: ${getResponsiveValue({
    mobile: '5px',
    tablet: '10px',
    laptop: '10px',
    desktop: '10px'
  })};
  flex: 1;
  overflow-y: auto;
  width: 100%;
  align-items: center;
  
  /* Scrollbar styling */
  &::-webkit-scrollbar {
    width: ${getResponsiveValue({
      mobile: '6px',
      tablet: '8px',
      laptop: '8px',
      desktop: '8px'
    })};
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 4px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.2);
    border-radius: 4px;
    
    &:hover {
      background: rgba(255, 255, 255, 0.3);
    }
  }
`;

const MessageInputContainer = styled.div`
  width: 100%;
  border-radius: 0px;
  padding: ${getResponsiveValue({
    mobile: '5px 10px 5px',
    tablet: '5px 15px 5px',
    laptop: '5px 5px 5px',
    desktop: '5px 5px 5px'
  })};
  margin-top: ${getResponsiveValue({
    mobile: '20px',
    tablet: '30px',
    laptop: '40px',
    desktop: '40px'
  })};
  position: relative;
  bottom: ${getResponsiveValue({
    mobile: '-10px',
    tablet: '-15px',
    laptop: '-20px',
    desktop: '-20px'
  })};
`;

const LoadingText = styled.div`
  color: white;
  font-family: 'VastagoGrotesk';
  font-weight: 400;
  font-size: ${getResponsiveValue({
    mobile: '14px',
    tablet: '16px',
    laptop: '16px',
    desktop: '16px'
  })};
  text-align: center;
  padding: ${getResponsiveValue({
    mobile: '15px',
    tablet: '20px',
    laptop: '20px',
    desktop: '20px'
  })};
`;

const NoMessagesText = styled.div`
  color: #000000 !important;
  font-family: 'VastagoGrotesk';
  font-weight: 400;
  font-size: ${getResponsiveValue({
    mobile: '14px',
    tablet: '16px',
    laptop: '16px',
    desktop: '16px'
  })};
  text-align: center;
  padding: ${getResponsiveValue({
    mobile: '15px',
    tablet: '20px',
    laptop: '20px',
    desktop: '20px'
  })};
  background-color: transparent;
`; 